import {
  ALL_SELECTED_FILTERS_KEY,
  UNASSIGNED_FILTER_VALUE,
} from "./filter-constants";

// The params forms seed marked all selected fields into their formData and
// DevExtreme mutates formData in place, so every form must work on a copy of
// reportInfo. Otherwise edits and seeding would leak into the report detail
// view's live state before (or without) a successful save.
//
// Time Interval shows "All Time" as a real default selection rather than a
// grayed placeholder, so seed the form's copy with it whenever no interval is
// stored (unbounded reports leave the key absent). Submit normalizes
// "All Time" back to an absent key, so the stored data model is unchanged.
export const cloneReportInfoForForm = (reportInfo) => {
  const reportParameters = { ...(reportInfo?.report_parameters || {}) };
  if (!reportParameters.time_interval) {
    reportParameters.time_interval = "All Time";
  }
  return { ...(reportInfo || {}), report_parameters: reportParameters };
};

// Read path counterpart to the submit-time interval handling: an unbounded
// report (no time interval, or the "All Time" default) must not carry stale
// start/end dates into a report query, or the backend date bounds a report the
// UI presents as "All Time" (buildDateRangeClause applies whatever dates are
// present). Reports saved before ENG-1417 can still hold those keys, so strip
// them here before sending report_parameters to any report query API. Returns
// a copy; never mutates the report detail state.
export const reportQueryParameters = (reportParameters) => {
  const params = { ...(reportParameters || {}) };
  if (!params.time_interval || params.time_interval === "All Time") {
    delete params.start_date;
    delete params.end_date;
  }
  return params;
};

// Derives the time-interval editor visibility from a report's saved "Time
// Interval" value. Shared by every report params form, both to seed the initial
// `editorVisiblity` state (so the custom Start/End Date boxes show up when
// editing a report whose interval is "Custom") and inside each form's Time
// Interval onValueChanged, so the two paths apply one rule. Pass the saved
// `reportInfo?.report_parameters?.time_interval` on init, or `e.value` on change.
// `dimension` is only consumed by forms that render a time-dimension field;
// forms without one take just `startDate`/`endDate`.
export const getTimeIntervalVisibility = (interval) => ({
  startDate: interval === "Custom",
  endDate: interval === "Custom",
  dimension: !!interval && interval !== "All Time",
});

// Handles a Time Interval selection. "All Time" is a real, selectable value
// meaning an unbounded range (the resting default), so choosing it keeps
// "All Time" displayed and wipes the dependent time fields, since dates and a
// time dimension are meaningless on an unbounded range. Clearing the box via
// the (x) reverts it to the "All Time" default rather than a blank/grayed
// placeholder. Call from every Time Interval onValueChanged and use the
// returned value for visibility state. When a non blank interval is chosen and
// the form has a Time Dimension field, an empty dimension defaults to
// `dimensionItems[0]` (the dimension is required for the interval to mean
// anything, and this matches the column the API falls back to).
export const applyTimeIntervalSelection = (
  e,
  paramsFormInstance,
  dimensionItems = []
) => {
  const updateField = (dataField, value) => {
    const items = paramsFormInstance?.option("items") || [];
    if (!items.some((item) => item?.dataField === dataField)) return;
    paramsFormInstance.updateData(dataField, value);
  };

  // "All Time" (or a cleared box) is the unbounded default: wipe the dependent
  // fields so a later interval choice starts fresh. A cleared box reverts to
  // "All Time" so the field never shows a blank state; that reset re-invokes
  // this handler with "All Time", which is an idempotent noop.
  if (!e.value || e.value === "All Time") {
    updateField("report_parameters.start_date", null);
    updateField("report_parameters.end_date", null);
    updateField("report_parameters.timestamp_dimension", null);
    if (!e.value) {
      e.component.option("value", "All Time");
    }
    return "All Time";
  }

  if (dimensionItems.length > 0) {
    const current =
      paramsFormInstance?.option("formData")?.report_parameters
        ?.timestamp_dimension;
    if (!current) {
      updateField("report_parameters.timestamp_dimension", dimensionItems[0]);
    }
  }

  return e.value;
};

// Normalizes the report filters at save time (ENG-1417). Each tag box
// filter ends up in one of three states:
//   - Default all: no values saved. An empty or untouched selection means
//     "no filter"; the params panel shows "All".
//   - All selected: no values saved, field name recorded under
//     ALL_SELECTED_FILTERS_KEY. Query behaves identically to default all
//     (rows with NULLs and values created later are included), but the edit
//     form reseeds the tag box fully checked.
//   - Subset: values saved as is (may include the unassigned sentinel).
// An explicit list of all current values must never be saved: rows with a
// NULL in the filtered column don't match a SQL IN list, and values created
// after the save would be silently excluded.
// Mutates reportParameters in place; pass a copy of the form's object so
// submit side effects never leak into form or report detail state. Call from
// the report popups' handleSubmit with the params form instance.
export const normalizeReportFilters = (
  paramsFormInstance,
  reportParameters
) => {
  if (!paramsFormInstance || !reportParameters) return;

  // Single value params (select boxes) go blank as null, via the clear (x)
  // button or an explicit "All" item. The blank state is an absent key, so
  // strip null/undefined here: a persisted null is a stray value the report
  // query APIs don't expect (e.g. getTimeEntryReport only treats invoiced
  // "Yes"/"No" as a filter). This keeps the ENG-1417 single-select "All"
  // state as a clean absent key.
  for (const key of Object.keys(reportParameters)) {
    if (reportParameters[key] === null || reportParameters[key] === undefined) {
      delete reportParameters[key];
    }
  }

  const prior = Array.isArray(reportParameters[ALL_SELECTED_FILTERS_KEY])
    ? reportParameters[ALL_SELECTED_FILTERS_KEY]
    : [];
  const allSelected = [];

  for (const item of paramsFormInstance.option("items") || []) {
    if (item?.editorType !== "dxTagBox" || !item?.dataField) continue;

    const paramKey = item.dataField.split(".").pop();
    const editor = paramsFormInstance.getEditor(item.dataField);
    const options = editor?.option("items");

    // Coverage is compared by value, not by count: a saved selection can
    // hold stale entries (dxTagBox keeps values that no longer exist in
    // items), so a length comparison could misread a deliberately partial
    // filter as "all selected". Every tag box in the report params forms
    // either sets a string valueExpr (saved values are scalars) or stores
    // user objects keyed by user_id; the unassigned sentinel participates
    // like any other option.
    //
    // DevExtreme defaults valueExpr to the literal "this" when a tag box sets
    // none (the user tag boxes: case_lead, examiner, reviewer, users), meaning
    // the value is the whole item object. Treat that like no valueExpr and key
    // on user_id, otherwise keyOf would read entry["this"] === undefined for
    // every option and selected value, the coverage check would always pass,
    // and any non empty selection would collapse to the all-selected marker.
    const valueExprOption = editor?.option("valueExpr");
    const valueExpr =
      valueExprOption && valueExprOption !== "this" ? valueExprOption : null;
    const keyOf = (entry) => {
      if (entry === null || typeof entry !== "object") return entry;
      if (valueExpr) return entry[valueExpr];
      return entry.user_id !== undefined
        ? entry.user_id
        : JSON.stringify(entry);
    };

    // Carry the field's prior state through unchanged (keep its marker and
    // any values) when the editor is unresolved: hidden (getEditor is
    // undefined), the lookup hasn't returned (no options), or only the
    // synchronous unassigned sentinel is present because every real option
    // comes from a still pending (or errored) lookup. Collapsing to
    // all-selected in that window would silently widen a sentinel-bearing
    // subset (e.g. organization: ["__unassigned__"]) to "All" the moment the
    // user saves before the lookup resolves. A field whose lookup genuinely
    // returns zero rows also lands here, which correctly preserves its saved
    // subset rather than treating "only the sentinel" as "all".
    const hasRealOption =
      Array.isArray(options) &&
      options.some((option) => keyOf(option) !== UNASSIGNED_FILTER_VALUE);
    if (!editor || !hasRealOption) {
      if (prior.includes(paramKey)) allSelected.push(paramKey);
      continue;
    }

    const selected = reportParameters[paramKey];

    // Unchecking everything resets the field to default all.
    if (!Array.isArray(selected) || selected.length === 0) {
      delete reportParameters[paramKey];
      continue;
    }

    const selectedKeys = new Set(selected.map(keyOf));
    if (options.every((option) => selectedKeys.has(keyOf(option)))) {
      delete reportParameters[paramKey];
      allSelected.push(paramKey);
    }
  }

  if (allSelected.length > 0) {
    reportParameters[ALL_SELECTED_FILTERS_KEY] = allSelected;
  } else {
    delete reportParameters[ALL_SELECTED_FILTERS_KEY];
  }
};
